import type {ValueType} from '@github-ui/custom-properties-types'
import {areEqualValues} from '@github-ui/custom-properties-types/helpers'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useState} from 'react'

interface Suggestion {
  singleSelectValues: string[]
  multiSelectValues: string[]
}

interface Result {
  suggestion: Suggestion | undefined
}

interface SuggestionVariables {
  propertyName: string
  description: string
  existingAllowedValues: string[]
}

interface MutationVariables extends SuggestionVariables {
  isRefreshing: boolean
  previousSuggestions: Suggestion[]
}

interface HookParams {
  endpointPath: string
  valueType: ValueType
  enabled: boolean
  onSuggestionSuccess: () => void
}

export function useAllowedValuesSuggester({endpointPath, valueType, enabled, onSuggestionSuccess}: HookParams) {
  const [previousSuggestions, setPreviousSuggestions] = useState<Suggestion[]>([])

  const mutation = useMutation<Result, Error, MutationVariables>({
    mutationKey: ['copilotSuggestAllowedValues', endpointPath],
    mutationFn: async variables => {
      const response = await verifiedFetchJSON(endpointPath, {
        method: 'POST',
        body: {
          propertyName: variables.propertyName,
          description: variables.description,
          existingAllowedValues: variables.existingAllowedValues,
          previousSuggestions: variables.previousSuggestions,
        },
      })

      if (!response.ok) {
        throw new Error()
      }

      return response.json()
    },
    onSuccess: data => {
      setPreviousSuggestions(prev => [...prev, data.suggestion || {singleSelectValues: [], multiSelectValues: []}])
      onSuggestionSuccess()
    },
  })

  const suggestion = mutation.data?.suggestion
  const isSelect = valueType === 'single_select' || valueType === 'multi_select'

  const suggestedAllowedValues = (() => {
    if (!suggestion || !isSelect) return []
    return valueType === 'single_select' ? suggestion.singleSelectValues : suggestion.multiSelectValues
  })()

  function suggest(variables: SuggestionVariables) {
    if (!enabled) {
      return
    }

    if (shouldFetchSuggestion(mutation.variables, variables)) {
      mutation.mutate({...variables, previousSuggestions: [], isRefreshing: false})
    }
  }

  function refresh(variables: SuggestionVariables) {
    if (enabled) {
      mutation.mutate({...variables, previousSuggestions, isRefreshing: true})
    }
  }

  return {
    suggestedAllowedValues,
    suggestAllowedValues: suggest,
    refreshAllowedValues: refresh,
    isRefreshingAllowedValues: mutation.isPending && mutation.variables.isRefreshing,
  }
}

const normalize = (value: string) => value.toLowerCase()
const containsSameValues = (arr1: string[], arr2: string[]) =>
  areEqualValues(arr1.map(normalize).toSorted(), arr2.map(normalize).toSorted())

function variablesAreDifferent(lastVars: SuggestionVariables | undefined, nextVars: SuggestionVariables) {
  if (!lastVars) {
    return true
  }

  return (
    lastVars.propertyName !== nextVars.propertyName ||
    lastVars.description !== nextVars.description ||
    !containsSameValues(lastVars.existingAllowedValues, nextVars.existingAllowedValues)
  )
}

const shouldFetchSuggestion = (lastVars: SuggestionVariables | undefined, nextVars: SuggestionVariables) =>
  (nextVars.propertyName.trim() || nextVars.description.trim()) && variablesAreDifferent(lastVars, nextVars)
